/**
 * Reading-progression engine: spaced-repetition over a language's 250-word pool.
 * See specs/word-mastery-progression.md §3–§4.
 *
 * Owns the per-player mastery state and feeds words to the board machinery. The Phaser
 * scenes call:
 *   beginSession()        once per round (game start)
 *   buildBoard()          to get the next board's rule + synthetic word set
 *   recordHit(word, ok)   per pop
 *   finishBoard()         when a board is cleared (marks correctly-avoided decoys)
 *   stats()               for the star bar / ABC-Planet progress
 */
import type { Pack, PackWord, PackSet, PackClass } from '@/data/packs/types';
import { MatchClass, MatchPictureWord, type BoardRule } from '@/game/rules/BoardRule';
import { wordInClass, wordClasses, canBeDecoy } from '@/data/packs/membership';
import { devParam } from '@/game/dev';
import {
  activePerDrillClass, ACTIVE_FUNCTION_WORDS, BOX_WEIGHTS, MAX_BOX, MASTER_BOX,
  DRILL_CLASSES, CORRECT_PER_BOARD, WRONG_PER_BOARD,
  MIN_CORRECT_PER_BOARD, WORDS_PER_STAR, STAR_COUNT, UFO_MAX, DIAMOND_MAX, ICE_MAX, GALAXY_MAX, SAT_MAX, CAT_MAX,
  WORM_MAX,
  ROCKET_MAX,
  PASTA_MAX,
  starsFor,
  SPACING_GAPS, NOT_DUE_WEIGHT, JUST_SHOWN_WEIGHT,
  MAX_SIGHTINGS_PER_GAME, OVER_SOFT_CAP_WEIGHT, HARD_SIGHTINGS_PER_GAME,
  TOPIC_UNLOCK_MASTERY,
} from './config';
import {
  loadMastery, saveMastery, resetMastery, migrateMasteryKey,
  type MasteryData, type WordStat, type WordState,
} from './store';
import { packKey } from '@/data/packs';
import { isReadingPool } from '@/data/pools';
import { masterableWords, isMasteredStat } from './lessonStats';
import { scoreboardOf, type Scoreboard } from './scoreboard';
import { milestoneReached } from '@/game/milestones';

export interface PoolStats {
  total: number;
  mastered: number;
  inProgress: number;
  locked: number;
  stars: number;            // 0..STAR_COUNT
  masteredTowardNextStar: number; // 0..WORDS_PER_STAR
  completion: number;       // mastered / total, 0..1
  coverage: number;         // introduced / total, 0..1
  accuracy: number;         // 0..1 (0 if no attempts)
  abcPlanetReached: boolean;
}

export interface SessionSummary {
  newlyMastered: string[];   // word texts mastered this session
  starsEarned: number;       // stars gained this session (clamped ≥ 0)
  stats: PoolStats;
  /**
   * A board cleared faster than anything before it in this lesson, in ms — null when this game beat
   * nothing. The first cleared board of a lesson counts: there was no time, and now there is one.
   */
  recordRoundMs: number | null;
  /**
   * Planet-stack stars of a run that reached a body no run of this lesson had reached before, or
   * null. Deliberately not "a bigger number than last time": 5 → 6 stars is often the same planet,
   * and a screen announcing Saturn twice teaches the child that the announcement means nothing.
   */
  recordStackStars: number | null;
  /** Games of THIS lesson including this one — 1 is the lesson's first game (first-game-summary.md §3). */
  sessionCount: number;
  /**
   * Words one step from a star: answered correctly enough to sit in the top boxes, held back only
   * because mastery needs a second session (first-game-summary.md §4.2). A count of words, not stars.
   */
  almostWords: number;
}

/**
 * Reconstructs the journey stats as they stood BEFORE this session's gains, so the UI can show
 * the pre-round bar and then animate up to `summary.stats`. (`stats` is the post-round snapshot.)
 */
export function preRoundStats(summary: SessionSummary): PoolStats {
  const prevMastered = Math.max(0, summary.stats.mastered - summary.newlyMastered.length);
  return {
    ...summary.stats,
    mastered: prevMastered,
    // Same formula as stats() — see starsFor(). Anything else disagrees with the rocket, which
    // rides `completion`, on every lesson whose size is not the 250-word reading pool.
    stars: starsFor(prevMastered, summary.stats.total),
    completion: summary.stats.total > 0 ? prevMastered / summary.stats.total : 0,
    abcPlanetReached: false,
  };
}

function freshStat(): WordStat {
  return {
    box: 1, state: 'locked', seen: 0, correct: 0, wrong: 0, streak: 0, bestStreak: 0,
    introducedAt: null, lastSeenAt: null, masteredAt: null, sessionsSeen: 0, lastSession: 0,
    lastBoardSeq: 0,
  };
}

function now(): number {
  return Date.now();
}

export class ReadingProgression {
  readonly lang: string;
  readonly pool: Pack;
  /** localStorage key for THIS lesson's mastery (pack id + lang) — see store.ts header. */
  private readonly storeKey: string;
  /** The journey universe: distinct words that can be mastered in this lesson. */
  private readonly words: PackWord[];
  private readonly classes: PackClass[];
  /** Classes a board rule may target for THIS pool (the rest are avoidance-only distractors). */
  private readonly drillClasses: readonly string[];
  private data: MasteryData;

  private boardIds: string[] = [];
  private resolved = new Set<string>();
  /**
   * Boards this word has appeared on in the CURRENT game — the one piece of board bookkeeping that
   * is deliberately not persisted. It backs the per-game ceiling (MAX_SIGHTINGS_PER_GAME), which
   * is about one sitting not feeling repetitive; carrying it across games would instead starve the
   * next game of the words the child is actually learning. Reset by beginSession().
   */
  private gameSightings = new Map<string, number>();

  private sessionStartStars = 0;
  private sessionNewlyMastered: string[] = [];
  /** Records BEATEN during the current game, for the summary screen. Reset by beginSession(). */
  private sessionRecordRoundMs: number | null = null;
  private sessionRecordStackStars: number | null = null;

  constructor(lang: string, pool: Pack) {
    this.lang = lang;
    this.pool = pool;
    this.storeKey = packKey(pool);
    // The journey universe = the lesson's masterable words (see masterableWords, which the lesson
    // picker reads too — the two must agree on how big a journey is).
    this.words = masterableWords(pool);
    this.classes = pool.classes;
    // A pool may declare its own drillable classes (e.g. Thai adds `classifier`, JA/KO add
    // `particle`); otherwise the default noun/verb/adjective applies. Classes outside this set
    // are never board targets — they're distractors mastered by correct avoidance.
    this.drillClasses = (pool.drillClasses && pool.drillClasses.length > 0)
      ? pool.drillClasses
      : DRILL_CLASSES;
    // Carry pre-per-lesson reading progress (keyed by bare language tag) onto its lesson key.
    // Only the reading journey ever had a legacy store, so other lessons must not inherit it.
    if (isReadingPool(pool)) migrateMasteryKey(lang, this.storeKey);
    this.data = loadMastery(this.storeKey);
  }

  // ── session lifecycle ──────────────────────────────────────────────────────

  beginSession(): void {
    this.data.sessionCounter += 1;
    const ts = now();
    if (this.data.profile.firstPlayedAt === null) this.data.profile.firstPlayedAt = ts;
    this.data.profile.lastPlayedAt = ts;
    const today = new Date(ts).toISOString().slice(0, 10);
    if (!this.data.profile.days.includes(today)) this.data.profile.days.push(today);
    this.sessionStartStars = this.stats().stars;
    this.sessionNewlyMastered = [];
    this.sessionRecordRoundMs = null;
    this.sessionRecordStackStars = null;
    this.gameSightings.clear();
    this.syncUfoRewards();
    this.persist();
  }

  getSessionSummary(): SessionSummary {
    const stats = this.stats();
    return {
      newlyMastered: [...this.sessionNewlyMastered],
      starsEarned: Math.max(0, stats.stars - this.sessionStartStars),
      stats,
      recordRoundMs: this.sessionRecordRoundMs,
      recordStackStars: this.sessionRecordStackStars,
      sessionCount: this.data.sessionCounter,
      almostWords: this.almostWordCount(),
    };
  }

  private almostWordCount(): number {
    let n = 0;
    for (const w of this.words) if (this.deriveState(this.statOf(this.idOf(w))) === 'review') n++;
    return n;
  }

  // ── end-of-journey celebration ───────────────────────────────────────────────
  // The full-screen CelebrationScene is owed, not scheduled. The planet is stamped mid-round on
  // the pop that masters the last word, but the screen can only be shown at an exit — and the
  // child may take an exit that has none (Back to the menu, or killing the app). So the debt is
  // persisted and every exit checks it, rather than being derived from "did it happen THIS
  // session", which silently forfeited the celebration for anyone who didn't play to game-over.

  /** True while the journey is complete but its celebration has not been shown yet. */
  celebrationPending(): boolean {
    const { abcPlanetReachedAt, celebrationShownAt } = this.data.profile;
    return abcPlanetReachedAt !== null && celebrationShownAt === null;
  }

  /**
   * Settle the debt — called by CelebrationScene, so the screen shows exactly once.
   *
   * No-op unless the planet really was reached: `?planet` forces the scene open on any game-over,
   * and stamping then would mark the celebration spent before the child ever earned it.
   */
  markCelebrationShown(): void {
    if (this.data.profile.abcPlanetReachedAt === null) return;
    if (this.data.profile.celebrationShownAt !== null) return;
    this.data.profile.celebrationShownAt = now();
    this.persist();
  }

  // ── finished journey: every minigame open ────────────────────────────────────
  // The six treats are the way IN to the six minigames, and every one of them is bought with a
  // deep run of the main game (50–213 smashes). That ladder is the right shape while a lesson is
  // being learned. It is the wrong shape once it HAS been: a child who mastered all 250 words has
  // already done more than any single run asks for, and telling them "now go reach Neptune again
  // if you want the Ice Maze" spends the reward on another toll.
  //
  // So finishing a lesson opens all six for it, permanently and for free — one of each on the
  // menu, and tapping one costs nothing (treatSlots in progression/treats.ts, read by each add*).
  // Deliberately scoped to the lesson: the journey is per-lesson, so the unlock is too, and a
  // second lesson still has its own ladder to climb.
  //
  // The banks are NOT touched by this. `claimX` is simply not called while the lesson is free, so
  // the earned/claimed counters keep whatever they held — a reset (which clears the planet stamp
  // too) puts the child back exactly where they were, not at zero treats.

  /**
   * This lesson's journey is finished, so its minigames are free to replay.
   *
   * Reads the same `abcPlanetReachedAt` stamp as celebrationPending() above — one fact, and the
   * unlock therefore arrives in the same instant the planet does, not a celebration later.
   */
  journeyDone(): boolean {
    return this.data.profile.abcPlanetReachedAt !== null;
  }

  // ── UFO reward bank ──────────────────────────────────────────────────────────
  // Spending one UFO "treat" launches a Survival run. Two ways to earn one, deliberately
  // independent: every 3 journey stars (the long game, mastery-driven) and reaching Mars in a
  // single run (the short game, score-driven — see grantStackUfo and UFO_AT_STACK_STARS).

  /** Bump the unlocked high-water mark to floor(stars/3). Monotonic — survives star regression. */
  private syncUfoRewards(): void {
    const u = Math.floor(this.stats().stars / 3);
    if (u > this.data.profile.ufosUnlocked) this.data.profile.ufosUnlocked = u;
  }

  /**
   * Award the once-per-run "reached Mars" UFO (RoundScene, UFO_AT_STACK_STARS).
   *
   * Banked in its OWN counter rather than added to `ufosUnlocked` — that field is a high-water
   * mark compared against floor(stars/3), so a bonus folded into it would raise the bar for the
   * star track and eat the next star-earned UFO. See the note in store.ts's ProfileStat.
   */
  grantStackUfo(): void {
    this.data.profile.ufosBonus += 1;
    this.persist();
  }

  /**
   * UFOs available to spend now, capped at UFO_MAX for display/spending (survival only). The bank's
   * high-water mark keeps growing with stars; spending one simply re-reveals the next earned UFO.
   * The menu calls this with no active session, so refresh + persist.
   */
  availableUfos(): number {
    const before = this.data.profile.ufosUnlocked;
    this.syncUfoRewards();
    if (this.data.profile.ufosUnlocked !== before) this.persist();
    const { ufosUnlocked, ufosBonus, ufosClaimed } = this.data.profile;
    const banked = ufosUnlocked + (ufosBonus ?? 0) - ufosClaimed;
    return Math.min(UFO_MAX, Math.max(0, banked));
  }

  /** Spend one UFO (on Survival launch). */
  claimUfo(): void {
    this.data.profile.ufosClaimed += 1;
    this.persist();
  }

  // ── 💎 Diamond bank (specs/pairs-mode.md §3.1) ───────────────────────────────
  // One source only — reaching Saturn in a single game — so a plain earned/claimed pair, with no
  // high-water mark to protect. Both fields are absent on profiles written before the feature.

  /** Award the once-per-run "reached Saturn" Diamond (RoundScene, DIAMOND_AT_STACK_STARS). */
  grantSaturnDiamond(): void {
    this.data.profile.diamondsEarned = (this.data.profile.diamondsEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Diamonds available to spend now, capped at DIAMOND_MAX for display/spending. The bank itself
   * is uncapped, so spending one simply re-reveals the next earned Diamond.
   */
  availableDiamonds(): number {
    const { diamondsEarned, diamondsClaimed } = this.data.profile;
    const banked = (diamondsEarned ?? 0) - (diamondsClaimed ?? 0);
    return Math.min(DIAMOND_MAX, Math.max(0, banked));
  }

  /** Spend one Diamond (on Pairs launch). */
  claimDiamond(): void {
    this.data.profile.diamondsClaimed = (this.data.profile.diamondsClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🧊 Ice Cube bank (specs/word-maze-mode.md §3.1) ───────────────────────────
  // One source only — reaching Neptune in a single game — so a plain earned/claimed pair, exactly
  // like the Diamond. Both fields are absent on profiles written before the feature.

  /** Award the once-per-run "reached Neptune" Ice Cube (RoundScene, ICE_AT_STACK_STARS). */
  grantNeptuneCube(): void {
    this.data.profile.cubesEarned = (this.data.profile.cubesEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Ice Cubes available to spend now, capped at ICE_MAX for display/spending. The bank itself is
   * uncapped, so spending one simply re-reveals the next earned cube.
   */
  availableCubes(): number {
    const { cubesEarned, cubesClaimed } = this.data.profile;
    const banked = (cubesEarned ?? 0) - (cubesClaimed ?? 0);
    return Math.min(ICE_MAX, Math.max(0, banked));
  }

  /** Spend one Ice Cube (on Ice Maze launch). */
  claimCube(): void {
    this.data.profile.cubesClaimed = (this.data.profile.cubesClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🐱 Cat bank (specs/cat-mouse-mode.md §3.1) ───────────────────────────────
  // One source only — reaching Venus in a single game — so a plain earned/claimed pair, exactly
  // like the Diamond and the Cube. The lowest rung of the treat ladder (25 smashes), which is the
  // whole point of it: the mode it buys is the one for a child who cannot yet read a word.
  // Both fields are absent on profiles written before the feature.

  /** Award the once-per-run "reached Venus" Cat (RoundScene, CAT_AT_STACK_STARS). */
  grantVenusCat(): void {
    this.data.profile.catsEarned = (this.data.profile.catsEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Cats available to spend now, capped at CAT_MAX for display/spending. The bank itself is
   * uncapped, so spending one simply re-reveals the next earned Cat.
   */
  availableCats(): number {
    const { catsEarned, catsClaimed } = this.data.profile;
    const banked = (catsEarned ?? 0) - (catsClaimed ?? 0);
    return Math.min(CAT_MAX, Math.max(0, banked));
  }

  /** Spend one Cat (on Cat & Mouse launch). */
  claimCat(): void {
    this.data.profile.catsClaimed = (this.data.profile.catsClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🪱 Space Worm bank (specs/space-worm-mode.md §3.1) ───────────────────────
  // One source only — reaching star 13, where the Sun reddens, in a single game — so a plain
  // earned/claimed pair, exactly like every bank since the Diamond. The top of the treat ladder
  // and the first prize above the full stack. Both fields are absent on older profiles.

  /** Award the once-per-run "reached the red Sun" Worm (RoundScene, WORM_AT_STACK_STARS). */
  grantRedSunWorm(): void {
    this.data.profile.wormsEarned = (this.data.profile.wormsEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Worms available to spend now, capped at WORM_MAX (two) for display/spending. The bank itself
   * is uncapped, so spending one simply re-reveals the next earned Worm.
   */
  availableWorms(): number {
    const { wormsEarned, wormsClaimed } = this.data.profile;
    const banked = (wormsEarned ?? 0) - (wormsClaimed ?? 0);
    return Math.min(WORM_MAX, Math.max(0, banked));
  }

  /** Spend one Worm (on Space Worm launch). */
  claimWorm(): void {
    this.data.profile.wormsClaimed = (this.data.profile.wormsClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🚀 Rocket bank (specs/rocket-read-mode.md §3.1) ─────────────────────────
  // One source only — reaching star 15, where the Sun turns violet, in a single game — so a plain
  // earned/claimed pair like every bank since the Diamond. Both fields are absent on older profiles.

  /** Award the once-per-run "reached the violet Sun" Rocket (RoundScene, ROCKET_AT_STACK_STARS). */
  grantVioletSunRocket(): void {
    this.data.profile.rocketsEarned = (this.data.profile.rocketsEarned ?? 0) + 1;
    this.persist();
  }

  /** Rockets available to spend now, capped at ROCKET_MAX (two). The bank itself is uncapped. */
  availableRockets(): number {
    const { rocketsEarned, rocketsClaimed } = this.data.profile;
    const banked = (rocketsEarned ?? 0) - (rocketsClaimed ?? 0);
    return Math.min(ROCKET_MAX, Math.max(0, banked));
  }

  /** Spend one Rocket (on Rocket Read launch). */
  claimRocket(): void {
    this.data.profile.rocketsClaimed = (this.data.profile.rocketsClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🌌 Galaxy bank (specs/wagon-mode.md §3.1) ────────────────────────────────
  // One source only — reaching the top of the planet ladder (GALAXY_STARS) in a single game — so a
  // plain earned/claimed pair, exactly like the Diamond and the Cube. Both fields are absent on
  // profiles written before the feature.

  /** Award the once-per-run "reached the galaxy" Galaxy (RoundScene, GALAXY_AT_STACK_STARS). */
  grantGalaxy(): void {
    this.data.profile.galaxiesEarned = (this.data.profile.galaxiesEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Galaxies available to spend now, capped at GALAXY_MAX (one) for display/spending. The bank
   * itself is uncapped, so a second one earned before the first is spent is not destroyed — it
   * re-reveals the moment the Wagon run is opened. At 213 smashes a piece, a swallowed treat here
   * is the most expensive bug any of these banks can have.
   */
  availableGalaxies(): number {
    const { galaxiesEarned, galaxiesClaimed } = this.data.profile;
    const banked = (galaxiesEarned ?? 0) - (galaxiesClaimed ?? 0);
    return Math.min(GALAXY_MAX, Math.max(0, banked));
  }

  /** Spend the Galaxy (on Word Wagon launch). */
  claimGalaxy(): void {
    this.data.profile.galaxiesClaimed = (this.data.profile.galaxiesClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🛰️ Satellite bank (specs/labyrinth-mode.md §3.1) ────────────────────────
  // One source only — filling the star stack in a single game — so a plain earned/claimed pair,
  // exactly like the Diamond, the Cube and the Galaxy. Both fields are absent on older profiles.

  /** Award the once-per-run "filled the solar system" Satellite (RoundScene, SAT_AT_STACK_STARS). */
  grantSunSatellite(): void {
    this.data.profile.satellitesEarned = (this.data.profile.satellitesEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Satellites available to spend now, capped at SAT_MAX (one) for display/spending. The bank
   * itself is uncapped, so a second earned before the first is spent is not destroyed — it
   * re-reveals the moment the Labyrinth run is opened.
   */
  availableSatellites(): number {
    const { satellitesEarned, satellitesClaimed } = this.data.profile;
    const banked = (satellitesEarned ?? 0) - (satellitesClaimed ?? 0);
    return Math.min(SAT_MAX, Math.max(0, banked));
  }

  /** Spend the Satellite (on Word Labyrinth launch). */
  claimSatellite(): void {
    this.data.profile.satellitesClaimed = (this.data.profile.satellitesClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🍝 Plate bank (specs/recipe-mode.md §3.1) ───────────────────────────────
  // One source only — star 14, the eclipse, in a single game — so a plain earned/claimed pair,
  // exactly like the Diamond, the Cube, the Galaxy and the Satellite. Both fields are absent on
  // older profiles.

  /** Award the once-per-run eclipse Plate (RoundScene, PASTA_AT_STACK_STARS). */
  grantEclipsePlate(): void {
    this.data.profile.platesEarned = (this.data.profile.platesEarned ?? 0) + 1;
    this.persist();
  }

  /**
   * Plates available to spend now, capped at PASTA_MAX (two) for display/spending. The bank itself
   * is uncapped, so a third earned before the first two are spent is not destroyed — it re-reveals
   * the moment a Recipe run is opened.
   */
  availablePlates(): number {
    const { platesEarned, platesClaimed } = this.data.profile;
    const banked = (platesEarned ?? 0) - (platesClaimed ?? 0);
    return Math.min(PASTA_MAX, Math.max(0, banked));
  }

  /** Spend a Plate (on Recipe Kitchen launch). */
  claimPlate(): void {
    this.data.profile.platesClaimed = (this.data.profile.platesClaimed ?? 0) + 1;
    this.persist();
  }

  // ── 🏆 Scoreboard (specs/scoreboard.md §4) ───────────────────────────────────
  // The lesson's record card, written from exactly two points in a game and read by the panel the
  // reward icon opens. Nothing here feeds back into the boards, the clock or the treats: these are
  // marks, not mechanics.

  /**
   * A board was cleared: count it, and keep the time if it beats this lesson's record.
   *
   * `elapsedMs` is RoundScene's pause-aware board clock, so time spent behind the help overlay or
   * with the app backgrounded is already excluded — a paused board must not be able to set a
   * record, and neither must it be able to spoil one.
   *
   * Cleared, not merely played: a board the child abandoned or lost their last heart on has no
   * clear time to record, so it cannot be a record and is not counted here (§4.2).
   */
  recordBoardCleared(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return;
    const p = this.data.profile;
    p.totalRounds = (p.totalRounds ?? 0) + 1;
    const ms = Math.round(elapsedMs);
    if (p.bestRoundMs === null || p.bestRoundMs === undefined || ms < p.bestRoundMs) {
      p.bestRoundMs = ms;
      this.sessionRecordRoundMs = ms;
    }
    this.persist();
  }

  /**
   * A game ended — from EITHER exit, hearts-out and Back alike. Raises the two high-water marks.
   *
   * `roundsReached` is the round number the game ended ON (RoundScene.boardNumber), which is the
   * number the HUD was showing at that moment; see §4.2 for why that is not the same count as
   * `totalRounds` and why the panel labels them differently rather than reconciling them.
   *
   * `stackStars` is the planet column's star count for the run (RoundScene.sessionStars) — the
   * ladder that reaches the galaxy at GALAXY_STARS, not the lesson's journey stars.
   */
  recordGameEnd(roundsReached: number, stackStars: number): void {
    const p = this.data.profile;
    if (Number.isFinite(roundsReached) && roundsReached > (p.maxRound ?? 0)) {
      p.maxRound = Math.floor(roundsReached);
    }
    if (Number.isFinite(stackStars) && stackStars > (p.bestStackStars ?? 0)) {
      const before = milestoneReached(p.bestStackStars ?? 0);
      p.bestStackStars = Math.floor(stackStars);
      const after = milestoneReached(p.bestStackStars);
      // Only a new BODY is news. The mark rises on every star, but the picture the child recognises
      // changes far less often, and that is the thing worth putting on the screen.
      if (after !== null && after !== before) this.sessionRecordStackStars = p.bestStackStars;
    }
    this.persist();
  }

  /** This lesson's record card. Same derivation the menu uses without an engine (scoreboard.ts). */
  scoreboard(): Scoreboard {
    return scoreboardOf(this.data.profile);
  }

  /**
   * Pool words ordered "already-learned first": introduced words (most-familiar first by Leitner
   * box, then exposure) ahead of not-yet-introduced ones (by frequency rank). Survival draws from
   * the front so kids re-read words they know, only reaching for new ones if it runs short.
   */
  survivalWords(): PackWord[] {
    const introduced: PackWord[] = [];
    const locked: PackWord[] = [];
    for (const w of this.words) (this.isIntroduced(w) ? introduced : locked).push(w);
    introduced.sort((a, b) => {
      const sa = this.statOf(this.idOf(a));
      const sb = this.statOf(this.idOf(b));
      return (sb.box - sa.box) || (sb.seen - sa.seen);
    });
    locked.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
    return [...introduced, ...locked];
  }

  // ── word-stat helpers ────────────────────────────────────────────────────────

  private statOf(id: string): WordStat {
    let s = this.data.words[id];
    if (!s) {
      s = freshStat();
      this.data.words[id] = s;
    }
    return s;
  }

  private deriveState(s: WordStat): WordState {
    if (s.introducedAt === null) return 'locked';
    if (isMasteredStat(s)) return 'mastered';
    if (s.box >= 3) return 'review';
    return 'learning';
  }

  private refreshState(id: string): void {
    const s = this.statOf(id);
    const next = this.deriveState(s);
    if (next === 'mastered' && s.state !== 'mastered') {
      if (s.masteredAt === null) s.masteredAt = now();
      this.sessionNewlyMastered.push(this.textOf(id));
    }
    s.state = next;
  }

  /**
   * What a word is CALLED when the game talks about it — today only the summary screen's "newly
   * mastered" list.
   *
   * `voll` is the whole word behind a tile that shows a gap: the "i oder ie?" lesson deals `B_ne`,
   * `F_sch`, `T_ger` (specs/ie-lesson.md §5), and a celebration screen reading "Neu gemeistert:
   * B_ne, F_sch" would congratulate a child with the riddle instead of the answer. Every other pool
   * leaves `voll` undefined and gets exactly the old behaviour — and this is the whole of that
   * lesson's engine change.
   */
  private textOf(id: string): string {
    const w = this.words.find((x) => x.id === id);
    return (typeof w?.voll === 'string' ? w.voll : w?.text) ?? id;
  }

  private isIntroduced(w: PackWord): boolean {
    return this.statOf(this.idOf(w)).introducedAt !== null;
  }

  private idOf(w: PackWord): string {
    // Pool words always carry a string id (see scripts/gen-reading-pools.mjs).
    return typeof w.id === 'string' ? w.id : w.text;
  }

  // ── introduction frontier ────────────────────────────────────────────────────

  private introduce(w: PackWord): void {
    const s = this.statOf(this.idOf(w));
    s.introducedAt = now();
    s.box = 1;
    s.state = 'learning';
  }

  private isFunctionWord(w: PackWord): boolean {
    // A distractor (pronoun/number/…) only if NONE of its classes is drillable — a creature that
    // is also a noun is a drill word, not a function word.
    return !wordClasses(w).some((c) => this.drillClasses.includes(c));
  }

  /** Introduced words of a class (any non-locked state) — eligible as correct targets. */
  private introducedOfClass(classId: string): PackWord[] {
    return this.words.filter((w) => wordInClass(w, classId) && this.isIntroduced(w));
  }

  /** Introduced-but-not-yet-mastered words of a class — what we keep topped up for variety. */
  private activeOfClass(classId: string): number {
    return this.words.reduce((n, w) => {
      if (!wordInClass(w, classId)) return n;
      const s = this.statOf(this.idOf(w));
      return n + (s.introducedAt !== null && this.deriveState(s) !== 'mastered' ? 1 : 0);
    }, 0);
  }

  private activeFunctionWords(): number {
    return this.words.reduce((n, w) => {
      if (!this.isFunctionWord(w)) return n;
      const s = this.statOf(this.idOf(w));
      return n + (s.introducedAt !== null && this.deriveState(s) !== 'mastered' ? 1 : 0);
    }, 0);
  }

  /** Lowest-rank locked word matching a predicate (pool is stored in ascending rank order). */
  private nextLocked(pred: (w: PackWord) => boolean): PackWord | null {
    for (const w of this.words) {
      if (pred(w) && this.statOf(this.idOf(w)).introducedAt === null) return w;
    }
    return null;
  }

  /**
   * True when a topic/drill class has been mastered *enough* to free its curriculum slot —
   * TOPIC_UNLOCK_MASTERY of its words, not all of them.
   *
   * All-or-nothing was the original rule and it deadlocked the curriculum: a topic keeps its slot
   * until the last of its ~14 words is mastered, mastery needs MIN_SESSIONS_TO_MASTER sessions,
   * and a single word the child keeps missing therefore blocked every remaining topic in the
   * lesson indefinitely. The stragglers stay in the rotation either way (they are still introduced
   * and still weighted up by their low box) — they just no longer hold the gate shut.
   *
   * An empty class is never "complete", or a pool with a typo'd class id would unlock everything.
   */
  private isClassComplete(classId: string): boolean {
    let total = 0;
    let mastered = 0;
    for (const w of this.words) {
      if (!wordInClass(w, classId)) continue;
      total += 1;
      if (this.deriveState(this.statOf(this.idOf(w))) === 'mastered') mastered += 1;
    }
    return total > 0 && mastered / total >= TOPIC_UNLOCK_MASTERY;
  }

  /**
   * Drill classes currently unlocked for introduction + boards. A pool may cap how many
   * *unmastered* topics are active at once via `maxActiveDrillClasses` (a gated curriculum):
   * we walk `drillClasses` in order, keeping topics until that many not-yet-complete ones are
   * in — so a sufficiently mastered topic (isClassComplete) frees its slot and the next locked
   * topic unlocks. Pools without the cap return all drill classes unchanged (reading/fables are
   * unaffected).
   */
  private unlockedDrillClasses(): readonly string[] {
    const cap = this.pool.maxActiveDrillClasses;
    if (!cap || cap <= 0) return this.drillClasses;
    const out: string[] = [];
    let activeUnmastered = 0;
    for (const c of this.drillClasses) {
      out.push(c);
      if (!this.isClassComplete(c)) activeUnmastered += 1;
      if (activeUnmastered >= cap) break;
    }
    return out;
  }

  /**
   * Keep each unlocked drill class stocked with active words (so every board type has variety and
   * the same words don't repeat), plus a trickle of function words for decoys and sight-word
   * exposure. As words are mastered, fresh ones are pulled in — this is what drives the journey
   * forward. Gated pools only stock the currently unlocked topics.
   *
   * The quota is per class but sized from how many classes are unlocked (activePerDrillClass), so
   * what a lesson actually puts in circulation is ~TARGET_CIRCULATION either way. A lesson with
   * three topics needs deeper topics than one with seven to keep MAX_SIGHTINGS_PER_GAME reachable.
   */
  private ensureActive(): void {
    const unlocked = this.unlockedDrillClasses();
    const quota = activePerDrillClass(unlocked.length);
    for (const c of unlocked) {
      while (this.activeOfClass(c) < quota) {
        const next = this.nextLocked((w) => wordInClass(w, c));
        if (!next) break;
        this.introduce(next);
      }
    }
    while (this.activeFunctionWords() < ACTIVE_FUNCTION_WORDS) {
      const next = this.nextLocked((w) => this.isFunctionWord(w));
      if (!next) break;
      this.introduce(next);
    }
  }

  /** Unlocked drill classes that currently have enough introduced words to form a board. */
  private eligibleDrillClasses(): string[] {
    return this.unlockedDrillClasses()
      .filter((c) => this.introducedOfClass(c).length >= MIN_CORRECT_PER_BOARD);
  }

  // ── board construction ───────────────────────────────────────────────────────

  buildBoard(): { rule: BoardRule; set: PackSet; pack: Pack } {
    this.ensureActive();
    // Advance the board clock first: every "last seen" comparison below is against this value.
    this.data.boardSeq += 1;

    // Round-robin across this pool's unlocked drill classes so board types are evenly distributed.
    // `lastClassId` is read from the store, not from memory, so a new game continues the cycle
    // instead of restarting it at drillClasses[0] on every single game.
    const eligible = this.eligibleDrillClasses();
    const order = this.unlockedDrillClasses();
    const start = (order.indexOf(this.data.lastClassId ?? '') + 1) % order.length;
    let classId = eligible[0] ?? order[0];
    for (let i = 0; i < order.length; i++) {
      const c = order[(start + i) % order.length];
      if (eligible.includes(c)) { classId = c; break; }
    }

    // Dev/staging only: `?board=<classId>` pins every round to that board type and treats the
    // whole pool as introduced, so promo/marketing captures can show any board (pirates, big
    // cats, …) on a fresh profile without grinding the journey first. See src/game/dev.ts.
    const forcedBoard = devParam('board');
    const bypassIntro = !!forcedBoard && this.classes.some((c) => c.id === forcedBoard);
    if (bypassIntro) classId = forcedBoard;
    this.data.lastClassId = classId;

    // How many boards pass between two turns of the same topic — the unit SPACING_GAPS is in.
    const cycleLen = Math.max(1, order.length);

    const correctPool = this.drawable(
      bypassIntro
        ? this.words.filter((w) => wordInClass(w, classId))
        : this.introducedOfClass(classId),
      CORRECT_PER_BOARD,
    );
    // Decoys must NOT belong to the target class — a creature is a noun, so it's never a wrong
    // answer on a Nomen board — nor to a class the target declares confusable via
    // decoyExcludeClasses, e.g. 'planets' words on a 'spaceflight' board. Both rules live in
    // canBeDecoy so Survival applies exactly the same filter.
    const targetClass = this.classes.find((c) => c.id === classId) ?? { id: classId };
    const wrongPool = this.drawable(
      this.words.filter((w) => canBeDecoy(w, targetClass) && (bypassIntro || this.isIntroduced(w))),
      WRONG_PER_BOARD,
    );

    const correct = this.weightedSample(correctPool, CORRECT_PER_BOARD, cycleLen);
    const wrong = this.weightedSample(wrongPool, WRONG_PER_BOARD, cycleLen);
    const boardWords = [...correct, ...wrong];

    // Mark seen / session exposure once per board for every shown word.
    this.boardIds = boardWords.map((w) => this.idOf(w));
    this.resolved = new Set();
    for (const id of this.boardIds) {
      this.markShown(id);
    }

    const cls = this.classes.find((c) => c.id === classId)
      ?? { id: classId, label: classId, prompt: classId };
    const set: PackSet = { id: `board-${classId}`, words: boardWords };
    this.persist();
    // Picture lessons keep every line above verbatim — same topic rotation, same Leitner-weighted
    // sampling, same decoy rules — and only swap what the banner asks. The sampled `correct` words
    // become the picture queue, so the board asks as many questions as it has targets.
    // See specs/picture-word-mode.md §4.3.
    const rule = this.pool.mode === 'picture-word'
      ? new MatchPictureWord(cls, correct)
      : new MatchClass(cls);
    return { rule, set, pack: this.pool };
  }

  /**
   * Tracking mode (arcade lessons): the board is built elsewhere (classic random-set or
   * banner-context), so we don't pick the words — we just observe the tiles that were shown.
   * Mirrors buildBoard's bookkeeping: introduce on first sight, mark seen + session exposure,
   * and arm the board for finishBoard() (unpopped decoys = correctly avoided). Call once per
   * board, with exactly the poppable tiles. No-op'd by RoundScene when the engine drives boards.
   */
  trackExternalBoard(shownWords: PackWord[]): void {
    this.data.boardSeq += 1;
    this.boardIds = shownWords.map((w) => this.idOf(w));
    this.resolved = new Set();
    for (const w of shownWords) {
      const id = this.idOf(w);
      if (this.statOf(id).introducedAt === null) this.introduce(w);
      this.markShown(id);
    }
    this.persist();
  }

  /** Record a single pop result for a shown word. */
  recordHit(word: PackWord, correct: boolean): void {
    const id = this.idOf(word);
    this.resolved.add(id);
    this.applyResult(id, correct);
    this.persist();
  }

  /** Cleared the board: words never popped were decoys correctly avoided → count as correct. */
  finishBoard(): void {
    for (const id of this.boardIds) {
      if (!this.resolved.has(id)) this.applyResult(id, true);
    }
    this.boardIds = [];
    this.resolved = new Set();
    this.syncUfoRewards();
    this.persist();
  }

  private applyResult(id: string, correct: boolean): void {
    const s = this.statOf(id);
    if (correct) {
      s.correct += 1;
      s.streak += 1;
      s.bestStreak = Math.max(s.bestStreak, s.streak);
      s.box = Math.min(MAX_BOX, s.box + 1);
      this.data.profile.totalCorrect += 1;
    } else {
      s.wrong += 1;
      s.streak = 0;
      s.box = s.box >= MASTER_BOX ? 3 : 1; // mastered slip → review; otherwise back to box 1
      this.data.profile.totalWrong += 1;
    }
    this.refreshState(id);
    this.checkAbcPlanet();
  }

  private checkAbcPlanet(): void {
    if (this.data.profile.abcPlanetReachedAt !== null) return;
    // The planet is "all of THIS lesson mastered". Reading pools are 250 words; arcade lessons
    // are smaller, so the target is the lesson's own size, not a fixed POOL_SIZE.
    if (this.words.length > 0 && this.masteredCount() >= this.words.length) {
      this.data.profile.abcPlanetReachedAt = now();
    }
  }

  // ── stats ──────────────────────────────────────────────────────────────────

  private masteredCount(): number {
    let n = 0;
    for (const w of this.words) if (this.deriveState(this.statOf(this.idOf(w))) === 'mastered') n++;
    return n;
  }

  stats(): PoolStats {
    let mastered = 0, learning = 0, review = 0, locked = 0;
    for (const w of this.words) {
      switch (this.deriveState(this.statOf(this.idOf(w)))) {
        case 'mastered': mastered++; break;
        case 'learning': learning++; break;
        case 'review': review++; break;
        default: locked++; break;
      }
    }
    const total = this.words.length;
    const attempts = this.data.profile.totalCorrect + this.data.profile.totalWrong;
    // Stars track completion (mastered / lesson size) so the 25-star bar fills evenly for any
    // lesson size. For the 250-word reading pool this equals floor(mastered / WORDS_PER_STAR).
    const completion = total > 0 ? mastered / total : 0;
    const stars = starsFor(mastered, total);
    const wordsPerStar = total > 0 ? total / STAR_COUNT : WORDS_PER_STAR;
    return {
      total,
      mastered,
      inProgress: learning + review,
      locked,
      stars,
      masteredTowardNextStar: Math.max(0, Math.round(mastered - stars * wordsPerStar)),
      completion,
      coverage: total > 0 ? (total - locked) / total : 0,
      accuracy: attempts > 0 ? this.data.profile.totalCorrect / attempts : 0,
      abcPlanetReached: this.data.profile.abcPlanetReachedAt !== null,
    };
  }

  // ── reset ────────────────────────────────────────────────────────────────────

  reset(): void {
    resetMastery(this.storeKey);
    this.data = loadMastery(this.storeKey);   // fresh blob → boardSeq 0, lastClassId null
    this.boardIds = [];
    this.gameSightings.clear();
    this.resolved = new Set();
    this.sessionNewlyMastered = [];
    this.sessionStartStars = 0;
  }

  // ── internals ────────────────────────────────────────────────────────────────

  /** Boards this word has already appeared on during the current game. */
  private sightingsOf(id: string): number {
    return this.gameSightings.get(id) ?? 0;
  }

  /**
   * Record that a word is on the board being built: board clock, per-game sighting count, and the
   * lifetime seen/session bookkeeping. One place, so buildBoard and trackExternalBoard cannot
   * drift on what "shown" means.
   */
  private markShown(id: string): void {
    const s = this.statOf(id);
    s.seen += 1;
    s.lastSeenAt = now();
    s.lastBoardSeq = this.data.boardSeq;
    this.gameSightings.set(id, this.sightingsOf(id) + 1);
    if (s.lastSession !== this.data.sessionCounter) {
      s.sessionsSeen += 1;
      s.lastSession = this.data.sessionCounter;
      this.refreshState(id); // crossing the session gate can complete mastery
    }
  }

  /**
   * Candidates for a board, with words already at the per-game hard ceiling removed — but only
   * while enough remain to fill the board. Filling the board wins over the ceiling; a short board
   * would be a visible bug, one extra sighting is not.
   */
  private drawable(pool: PackWord[], n: number): PackWord[] {
    const under = pool.filter((w) => this.sightingsOf(this.idOf(w)) < HARD_SIGHTINGS_PER_GAME);
    return under.length >= n ? under : pool;
  }

  /**
   * Spawn weight: the Leitner box weight (how *often*), scaled down when the word is not yet due
   * (how far *apart* — SPACING_GAPS) or has already had its share of this game.
   *
   * Everything here damps rather than excludes, so the sampler always has something to draw and a
   * thin topic degrades to "least recently seen first" instead of failing.
   *
   * @param cycleLen boards between two turns of the same topic — the unit SPACING_GAPS is in.
   */
  private weightFor(w: PackWord, cycleLen: number): number {
    const id = this.idOf(w);
    const s = this.statOf(id);
    const boxIdx = Math.min(Math.max(s.box, 1), MAX_BOX) - 1;
    let weight = BOX_WEIGHTS[boxIdx] ?? 1;

    // Spacing. lastBoardSeq 0 = never shown, which is maximally due.
    const last = s.lastBoardSeq ?? 0;
    if (last > 0) {
      const since = this.data.boardSeq - last;
      const gap = (SPACING_GAPS[boxIdx] ?? 1) * cycleLen;
      if (since <= 1) weight *= JUST_SHOWN_WEIGHT;      // was on the previous board
      else if (since < gap) weight *= NOT_DUE_WEIGHT;
    }

    // Per-game ceiling (the hard one is applied in drawable()).
    if (this.sightingsOf(id) >= MAX_SIGHTINGS_PER_GAME) weight *= OVER_SOFT_CAP_WEIGHT;

    return weight;
  }

  /** Sample up to n words without replacement, weighted by weightFor(). */
  private weightedSample(pool: PackWord[], n: number, cycleLen: number): PackWord[] {
    const items = [...pool];
    const out: PackWord[] = [];
    while (out.length < n && items.length > 0) {
      const weights = items.map((w) => this.weightFor(w, cycleLen));
      const total = weights.reduce((a, b) => a + b, 0);
      let idx: number;
      if (total <= 0) {
        idx = Math.floor(Math.random() * items.length);
      } else {
        let r = Math.random() * total;
        idx = 0;
        while (idx < items.length) {
          r -= weights[idx];
          if (r <= 0) break;
          idx++;
        }
        if (idx >= items.length) idx = items.length - 1;
      }
      out.push(items.splice(idx, 1)[0]);
    }
    return out;
  }

  private persist(): void {
    saveMastery(this.storeKey, this.data);
  }
}
